import React, { useState } from 'react';
import { Card, Table, Input, Select, Button, Avatar, Badge, Tag } from 'antd';
import { 
  SearchOutlined, 
  UserAddOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import './Team.css';

const { Option } = Select;

const Team = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  // Mock team member data
  const teamMembers = [
    {
      id: 1,
      name: 'John Mitchell',
      email: 'john.mitchell@pathmarkr.com',
      role: 'Owner',
      status: 'active',
      locations: ['All Locations'],
      lastActive: '2 hours ago',
      avatar: null
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      email: 'sarah.johnson@pathmarkr.com',
      role: 'Admin',
      status: 'active',
      locations: ['Nashville', 'Franklin', '+1'],
      lastActive: '1 day ago',
      avatar: null
    },
    {
      id: 3,
      name: 'Mike Thompson',
      email: 'mike.thompson@pathmarkr.com',
      role: 'Manager',
      status: 'active',
      locations: ['Memphis', 'Atlanta'],
      lastActive: '3 hours ago',
      avatar: null
    },
    {
      id: 4,
      name: 'Emily Davis',
      email: 'emily.davis@pathmarkr.com',
      role: 'Manager',
      status: 'active',
      locations: ['Clarksville', 'Hendersonville'],
      lastActive: '5 hours ago',
      avatar: null
    },
    {
      id: 5,
      name: 'David Wilson',
      email: 'david.wilson@pathmarkr.com',
      role: 'Viewer',
      status: 'invited',
      locations: ['Marietta', 'Gallatin'],
      lastActive: 'Never',
      avatar: null
    },
    {
      id: 6,
      name: 'Lisa Anderson',
      email: 'lisa.anderson@pathmarkr.com',
      role: 'Viewer',
      status: 'inactive',
      locations: ['Nashville'],
      lastActive: '30 days ago',
      avatar: null
    }
  ];

  const getRoleColor = (role) => {
    switch (role) {
      case 'Owner': return 'purple';
      case 'Admin': return 'blue';
      case 'Manager': return 'green';
      case 'Viewer': return 'default';
      default: return 'default';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return <Badge status="success" text="Active" />;
      case 'invited': return <Badge status="warning" text="Invited" />;
      case 'inactive': return <Badge status="default" text="Inactive" />;
      default: return <Badge status="default" text={status} />;
    }
  };

  const columns = [
    {
      title: 'Member',
      dataIndex: 'name',
      key: 'name',
      width: 300,
      render: (name, record) => (
        <div className="member-cell">
          <Avatar size={40} style={{ backgroundColor: '#3b82f6', marginRight: 12 }}>
            {name.split(' ').map(n => n[0]).join('')}
          </Avatar>
          <div>
            <div className="member-name">{name}</div>
            <div className="member-email">{record.email}</div>
          </div>
        </div>
      )
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role) => <Tag color={getRoleColor(role)}>{role}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusBadge(status)
    },
    {
      title: 'Locations',
      dataIndex: 'locations',
      key: 'locations',
      width: 250,
      render: (locations) => (
        <div className="locations-cell">
          {locations.map((loc, idx) => (
            <Tag key={idx} className="location-tag">{loc}</Tag>
          ))}
        </div>
      )
    },
    {
      title: 'Last Active',
      dataIndex: 'lastActive',
      key: 'lastActive',
      width: 150,
      render: (lastActive) => (
        <span className="last-active">
          <ClockCircleOutlined style={{ marginRight: 6 }} />
          {lastActive}
        </span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: () => (
        <Button type="link" size="small">•••</Button>
      )
    }
  ];

  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || member.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  return (
    <div className="team-management-page">
      {/* Header */}
      <div className="team-header">
        <div>
          <h1 className="page-title">Team Management</h1>
          <p className="page-subtitle">Manage team members and access permissions</p>
        </div>
        <div className="last-updated">Last updated: 6:49:54 PM</div>
      </div>

      {/* Title Bar */}
      <div className="team-title-bar">
        <div>
          <h2 className="section-title">Team Management</h2>
          <p className="section-subtitle">Manage team members and their access permissions</p>
        </div>
        <Button type="primary" icon={<UserAddOutlined />} size="large">
          Invite Member
        </Button>
      </div>

      {/* Team Members Count */}
      <div className="team-count">
        <h3>Team Members</h3>
        <p>{teamMembers.length} members</p>
      </div>

      {/* Search and Filters */}
      <div className="team-filters">
        <Input
          placeholder="Search team members..."
          prefix={<SearchOutlined />}
          size="large"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: 400 }}
        />
        <Select
          value={roleFilter}
          onChange={setRoleFilter}
          size="large"
          style={{ width: 200 }}
        >
          <Option value="all">All Roles</Option>
          <Option value="Owner">Owner</Option>
          <Option value="Admin">Admin</Option>
          <Option value="Manager">Manager</Option>
          <Option value="Viewer">Viewer</Option>
        </Select>
      </div>

      {/* Team Members Table */}
      <Card className="team-table-card">
        <Table
          columns={columns}
          dataSource={filteredMembers}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </div>
  );
};

export default Team;
